import { Euler, Object3D } from 'three';
import { StandardViewNodes } from './ViewBuilder';
import { ViewNode } from '../nodes/ViewNode';
import { defaultJP, defaultPosition, defaultRotation } from '@canvas-logic/clp-domain';
export class ViewNodeBuilder {
    constructor(viewBuilder, assetsManager, parentBuilder) {
        this.viewBuilder = viewBuilder;
        this.assetsManager = assetsManager;
        this.innerNodes = [];
        this.innerNodesKeys = new Set();
        this.nodesConnections = new Map();
        this.builders = new Map();
        this.connectActions = [];
        this.nodeRegistry = new Map();
        this.object3dRegistry = new Map();
        this.parentBuilder = parentBuilder;
    }
    clear() {
        this.innerNodes = [];
        this.builders.clear();
        this.innerNodesKeys.clear();
        this.nodesConnections.clear();
    }
    addViewNode(options) {
        const node = this.viewBuilder.createViewNode(options);
        this.attachNodeInstance(node);
        return node;
    }
    attachNodeInstance(node) {
        this.innerNodes.push(node);
        this.innerNodesKeys.add(node.key);
        this.nodeRegistry.set(node.key, node);
        const innerBuilder = new ViewNodeBuilder(this.viewBuilder, this.assetsManager, this);
        this.builders.set(node.key, innerBuilder);
        node.build({ builder: innerBuilder, context: this.viewBuilder.getContext() });
    }
    addModel3dNode(options) {
        return this.addViewNode({ type: StandardViewNodes.Model3d, key: options.key, params: options.params });
    }
    addComponentNode(options) {
        return this.addViewNode({ type: StandardViewNodes.Component, key: options.key, params: options.params });
    }
    addLightNode(options) {
        return this.addViewNode({ type: StandardViewNodes.Light, key: options.key, params: options.params });
    }
    connectNodes(params) {
        this.connectActions.push(() => {
            const parentJP = params.parent.jp;
            const childJP = params.child.jp;
            const parentKey = params.parent.key;
            const childKey = params.child.key;
            if (!this.innerNodesKeys.has(childKey)) {
                throw new Error(`No node with key ${childKey}`);
            }
            if (!this.innerNodesKeys.has(parentKey)) {
                throw new Error(`No node with key ${parentKey}`);
            }
            this.addNodeConnection(parentKey, { childKey, childJP, parentJP });
        });
    }
    mountTo(root) {
        var _a, _b, _c;
        const map = new Map();
        this.innerNodes.forEach((innerNode) => {
            const contentLayer = createContentLayer(innerNode);
            contentLayer.visible = innerNode.visible;
            const nativeLayer = createNativeLayer(innerNode);
            innerNode.mount({
                container: nativeLayer,
                context: this.viewBuilder.getContext(),
                assetsApi: this.assetsManager,
                materialsApi: this.viewBuilder.materialsManager,
            });
            const builder = this.builders.get(innerNode.key);
            if (!builder) {
                throw new Error(`Cannot find builder for node ${innerNode.key}`);
            }
            const innerLayer = createInnerLayer(innerNode);
            builder.mountTo(innerLayer);
            contentLayer.add(nativeLayer);
            contentLayer.add(innerLayer);
            const containerLayer = createContainerLayer(innerNode);
            containerLayer.add(contentLayer);
            containerLayer.position.set(innerNode.position.x, innerNode.position.y, innerNode.position.z);
            containerLayer.rotation.set(innerNode.rotation.x, innerNode.rotation.y, innerNode.rotation.z);
            containerLayer.scale.set(innerNode.scale.x, innerNode.scale.y, innerNode.scale.z);
            map.set(innerNode.key, containerLayer);
            this.object3dRegistry.set(innerNode.key, containerLayer);
            root.add(containerLayer);
        });
        this.connectActions.forEach((action) => action());
        for (const [parentKey, connections] of this.nodesConnections.entries()) {
            const parentContainer = map.get(parentKey);
            if (!parentContainer) {
                throw new Error(`Cannot find parent ${parentKey}`);
            }
            for (const { childKey, childJP, parentJP } of connections) {
                const childContainer = map.get(childKey);
                if (!childContainer) {
                    throw new Error(`Cannot find child ${childKey}`);
                }
                let childrenLayer = parentContainer.getObjectByName(getChildrenLayerName(parentKey));
                if (!childrenLayer) {
                    childrenLayer = createChildrenLayer(parentKey);
                    parentContainer.add(childrenLayer);
                }
                if (!childJP && !parentJP) {
                    childrenLayer.add(childContainer);
                }
                else {
                    const parentJpPosition = (_a = parentJP === null || parentJP === void 0 ? void 0 : parentJP.jointPoint.position) !== null && _a !== void 0 ? _a : defaultPosition;
                    const parentJpRotation = (_b = parentJP === null || parentJP === void 0 ? void 0 : parentJP.jointPoint.rotation) !== null && _b !== void 0 ? _b : defaultRotation;
                    const jpLayer = new Object3D();
                    jpLayer.name = (_c = parentJP === null || parentJP === void 0 ? void 0 : parentJP.name) !== null && _c !== void 0 ? _c : `JP_${parentKey}=>${childKey}`;
                    jpLayer.position.set(parentJpPosition.x, parentJpPosition.y, parentJpPosition.z);
                    jpLayer.rotation.set(parentJpRotation.x, parentJpRotation.y, parentJpRotation.z);
                    childrenLayer.add(jpLayer);
                    const matrix = getChildMatrix((childJP === null || childJP === void 0 ? void 0 : childJP.jointPoint) || defaultJP);
                    matrix.decompose(childContainer.position, childContainer.quaternion, childContainer.scale);
                    jpLayer.add(childContainer);
                }
            }
        }
        this.connectActions = [];
        this.applyAllModifiers();
    }
    addNodeConnection(parentKey, connection) {
        const connections = this.nodesConnections.get(parentKey);
        if (!connections) {
            this.nodesConnections.set(parentKey, [connection]);
        }
        else {
            connections.push(connection);
        }
    }
    collectAllNodes() {
        const allNodes = [...this.innerNodes];
        for (const builder of this.builders.values()) {
            allNodes.push(...builder.collectAllNodes());
        }
        return allNodes;
    }
    getAllNodeRegistries() {
        const registry = new Map(this.nodeRegistry);
        for (const builder of this.builders.values()) {
            for (const [key, node] of builder.getAllNodeRegistries()) {
                registry.set(key, node);
            }
        }
        return registry;
    }
    getAllObject3DRegistries() {
        const registry = new Map(this.object3dRegistry);
        for (const builder of this.builders.values()) {
            for (const [key, obj] of builder.getAllObject3DRegistries()) {
                registry.set(key, obj);
            }
        }
        return registry;
    }
    buildDependencyGraph(nodes) {
        const graph = new Map();
        for (const node of nodes) {
            const deps = new Set();
            for (const modifier of node.getModifiers()) {
                if (modifier.getDependencies) {
                    modifier.getDependencies().forEach((dep) => deps.add(dep));
                }
            }
            graph.set(node.key, deps);
        }
        return graph;
    }
    validateDependencies(graph) {
        for (const [nodeKey, deps] of graph) {
            for (const dep of deps) {
                if (!graph.has(dep)) {
                    throw new Error(`Modifier dependency error: Node "${nodeKey}" depends on non-existent node "${dep}"`);
                }
            }
        }
    }
    topologicalSort(graph) {
        const sorted = [];
        const visited = new Set();
        const visiting = new Set();
        const visit = (nodeKey, path = []) => {
            if (visited.has(nodeKey))
                return;
            if (visiting.has(nodeKey)) {
                const cycle = [...path, nodeKey].join(' → ');
                console.warn(`[ViewNodeBuilder] Circular dependency detected: ${cycle}`);
                return;
            }
            visiting.add(nodeKey);
            const deps = graph.get(nodeKey) || new Set();
            for (const dep of deps) {
                visit(dep, [...path, nodeKey]);
            }
            visiting.delete(nodeKey);
            visited.add(nodeKey);
            sorted.push(nodeKey);
        };
        for (const nodeKey of graph.keys()) {
            visit(nodeKey);
        }
        return sorted;
    }
    applyAllModifiers() {
        if (this.parentBuilder) {
            return;
        }
        const context = {
            findNodeByKey: (key) => this.getAllObject3DRegistries().get(key),
            getViewNode: (key) => this.getAllNodeRegistries().get(key),
        };
        const allNodes = this.collectAllNodes();
        const dependencyGraph = this.buildDependencyGraph(allNodes);
        try {
            this.validateDependencies(dependencyGraph);
        }
        catch (error) {
            console.error('[ViewNodeBuilder] Modifier dependency validation failed:', error);
            throw error;
        }
        const sortedKeys = this.topologicalSort(dependencyGraph);
        const allNodeRegistries = this.getAllNodeRegistries();
        const allObject3DRegistries = this.getAllObject3DRegistries();
        for (const nodeKey of sortedKeys) {
            const viewNode = allNodeRegistries.get(nodeKey);
            const obj3d = allObject3DRegistries.get(nodeKey);
            if (viewNode && obj3d) {
                const modifiers = viewNode.getModifiers();
                if (modifiers.length > 0) {
                    try {
                        viewNode.applyModifiers(obj3d, context);
                    }
                    catch (error) {
                        console.error(`[ViewNodeBuilder] Failed to apply modifiers for node "${nodeKey}":`, error);
                    }
                }
            }
        }
    }
}
function createContentLayer(innerNode) {
    const node = new Object3D();
    node.name = ViewNode.getContentNodeName(innerNode.key);
    return node;
}
function createContainerLayer(innerNode) {
    const node = new Object3D();
    node.name = ViewNode.getContainerNodeName(innerNode.key);
    return node;
}
function getChildrenLayerName(key) {
    return key + '_children';
}
function createChildrenLayer(key) {
    const node = new Object3D();
    node.name = getChildrenLayerName(key);
    return node;
}
function createNativeLayer(innerNode) {
    const node = new Object3D();
    node.name = innerNode.key + '_native';
    return node;
}
function createInnerLayer(innerNode) {
    const node = new Object3D();
    node.name = innerNode.key + '_inner';
    return node;
}
function getChildMatrix(jp) {
    const obj = new Object3D();
    obj.position.set(jp.position.x, jp.position.y, jp.position.z);
    const euler = new Euler();
    euler.set(jp.rotation.x, jp.rotation.y, jp.rotation.z);
    obj.rotation.copy(euler);
    obj.updateMatrix();
    return obj.matrix.clone().invert();
}
//# sourceMappingURL=ViewNodeBuilder.js.map